import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Upload, Download, X } from "lucide-react";
import api, { apiError, fmtDateLong } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function JadwalShiftTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [filterDate, setFilterDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ user_id: "", outlet_id: "", shift_id: "", schedule_date: today });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadMasters = async () => {
    const [u, o, s] = await Promise.all([api.get("/users"), api.get("/outlets"), api.get("/shifts")]);
    setUsers(u.data); setOutlets(o.data); setShifts(s.data);
  };
  const loadSchedules = async (scheduleDate = filterDate) => {
    const { data } = await api.get(`/schedules?date=${scheduleDate}`);
    setSchedules(data);
  };

  useEffect(() => { loadMasters().finally(() => setLoading(false)); }, []);
  useEffect(() => { loadSchedules(); }, [filterDate]);

  const resetForm = () => {
    setEditing(null);
    setForm({ user_id: "", outlet_id: "", shift_id: "", schedule_date: filterDate });
  };

  const openEdit = (schedule) => {
    setEditing(schedule);
    setForm({
      user_id: schedule.user_id,
      outlet_id: schedule.outlet_id,
      shift_id: schedule.shift_id,
      schedule_date: schedule.schedule_date,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.user_id || !form.outlet_id || !form.shift_id || !form.schedule_date)
      return toast.error("Lengkapi semua field jadwal");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/schedules/${editing.id}`, form);
        toast.success("Jadwal diperbarui");
        setFilterDate(form.schedule_date);
        await loadSchedules(form.schedule_date);
      } else {
        await api.post("/schedules", form);
        toast.success("Jadwal ditambahkan");
        await loadSchedules();
      }
      resetForm();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus jadwal ini?")) return;
    try { await api.delete(`/schedules/${id}`); toast.success("Jadwal dihapus"); await loadSchedules(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/schedules/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "template_jadwal_shift.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/schedules/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Berhasil: ${data.created} jadwal dibuat, ${data.skipped} dilewati`);
      if (data.errors?.length) toast.error(`${data.errors.length} baris gagal: ${data.errors.slice(0, 3).join("; ")}`);
      await loadSchedules();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#3368A0]" /></div>;

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-bold text-slate-800">Jadwal Shift</h2>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="font-semibold text-slate-700">
            {editing ? "Edit Jadwal" : "Tambah Jadwal"}
          </p>
          {editing && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="cancel-edit-jadwal-button"
              onClick={resetForm}
              className="text-slate-500 hover:text-slate-800"
            >
              <X className="mr-1.5 h-4 w-4" />
              Batal Edit
            </Button>
          )}
        </div>
        <div className="grid sm:grid-cols-5 gap-3 items-end">
          <div className="space-y-1.5"><Label>Karyawan</Label>
            <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
              <SelectTrigger data-testid="jadwal-user-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>{users.filter((u) => u.status === "aktif").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Outlet</Label>
            <Select value={form.outlet_id} onValueChange={(v) => setForm({ ...form, outlet_id: v })}>
              <SelectTrigger data-testid="jadwal-outlet-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>{outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Shift</Label>
            <Select value={form.shift_id} onValueChange={(v) => setForm({ ...form, shift_id: v })}>
              <SelectTrigger data-testid="jadwal-shift-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>{shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start_time}-{s.end_time})</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Tanggal</Label>
            <Input type="date" data-testid="jadwal-date-input" value={form.schedule_date} onChange={(e) => setForm({ ...form, schedule_date: e.target.value })} /></div>
          <Button
            data-testid={editing ? "save-jadwal-button" : "add-jadwal-button"}
            onClick={save}
            disabled={saving}
            className="bg-[#3368A0] hover:bg-[#284f7a]"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : editing ? (
              "Simpan Perubahan"
            ) : (
              <><Plus className="h-4 w-4 mr-1.5" /> Tambah</>
            )}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#C8DFDB] p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-semibold text-slate-700">Upload Massal (XLS)</p>
            <p className="text-xs text-slate-500 mt-0.5">Buat banyak jadwal sekaligus. Kolom: username, outlet, shift, tanggal (YYYY-MM-DD).</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button">
              <Download className="h-4 w-4 mr-1.5" /> Download Template
            </Button>
            <label>
              <input type="file" accept=".xlsx,.xls" onChange={uploadFile} className="hidden" data-testid="upload-xls-input" />
              <span className="inline-flex items-center cursor-pointer rounded-md bg-[#3368A0] hover:bg-[#284f7a] text-white text-sm font-medium h-9 px-4 transition-colors">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" /> Upload XLS</>}
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Label className="text-slate-600">Lihat jadwal tanggal:</Label>
        <Input type="date" data-testid="jadwal-filter-date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="w-44" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <p className="px-5 py-3 text-sm font-medium text-slate-600 bg-slate-50">{fmtDateLong(filterDate)}</p>
        <table className="w-full text-sm" data-testid="jadwal-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-5 py-3">Karyawan</th>
              <th className="text-left px-5 py-3">Outlet</th>
              <th className="text-left px-5 py-3">Shift</th>
              <th className="text-right px-5 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {schedules.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 py-8">Belum ada jadwal pada tanggal ini.</td></tr>}
            {schedules.map((s) => (
              <tr key={s.id} className="border-t border-slate-100">
                <td className="px-5 py-3 font-medium text-slate-700">{s.user_name}</td>
                <td className="px-5 py-3 text-slate-500">{s.outlet_name}</td>
                <td className="px-5 py-3 text-slate-500">{s.shift_name} <span className="font-mono text-xs text-slate-400">({s.shift_time})</span></td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => openEdit(s)}
                    data-testid={`edit-jadwal-${s.id}`}
                    title="Edit jadwal"
                    className="p-1.5 text-[#3368A0] hover:text-sky-800"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => remove(s.id)}
                    data-testid={`delete-jadwal-${s.id}`}
                    title="Hapus jadwal"
                    className="p-1.5 text-rose-500 hover:text-rose-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
